import React, { useEffect, useState } from "react";
import { Pencil, Trash2, ShieldCheck, Ban, Clock, Settings2, Plus, Tags, UserX, IdCard } from "lucide-react";
import { UserFichaDialog, PagoBadge, fmtFecha } from "../../components/admin/UserFichaDialog";
import { toast } from "sonner";
import { adminApi, apiError, isSuperAdmin } from "../../lib/api";
import { programs } from "../../mock/mock";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const PERIODOS = [["", "Todo el tiempo"], ["dia", "Hoy"], ["semana", "Esta semana"], ["mes", "Este mes"], ["bimestre", "Este bimestre"], ["trimestre", "Este trimestre"]];
const TIPO_OPTIONS = [["todos", "Todos"], ["generales", "Usuarios Generales"], ["padres", "Padres y Madres"], ["instructores", "Instructores"], ["suspensiones", "Suspensiones"], ["bajas", "Bajas"], ["nuevos", "Nuevos Usuarios"], ["invitados", "Invitados"], ["reclutamiento", "En Reclutamiento"], ["rol", "Por rol específico"]];
const GRUPO_LETRAS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [active, setActive] = useState(null); // active segment obj
  const [periodo, setPeriodo] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [fichaId, setFichaId] = useState(null);
  const [rbacUser, setRbacUser] = useState(null);
  const canRbac = isSuperAdmin();
  const [toDelete, setToDelete] = useState(null);
  const [manageRoles, setManageRoles] = useState(false);
  const [manageSegs, setManageSegs] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkGrupoVal, setBulkGrupoVal] = useState("");

  const loadMeta = async () => {
    try {
      const [segs, rs] = await Promise.all([adminApi.userSegments.list(), adminApi.userRoles.list()]);
      const activeSegs = segs.filter((s) => s.activo !== false);
      setSegments(activeSegs);
      setRoles(rs);
      setActive((a) => a || activeSegs[0] || { tipo: "todos", rol: "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = (seg = active, per = periodo) => {
    if (!seg) return;
    setLoading(true);
    adminApi.listUsers({ segmento: seg.tipo, rol: seg.rol || "", periodo: per })
      .then(setUsers).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { if (active) { setSelected(new Set()); load(active, periodo); } /* eslint-disable-next-line */ }, [active, periodo]);

  const filtered = users.filter((u) =>
    `${u.nombre} ${u.apellidos} ${u.email} ${u.programa} ${u.pais} ${u.ciudad} ${u.whatsapp} ${u.colegio} ${u.rol_supera}`.toLowerCase().includes(q.toLowerCase())
  );

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const visibleIds = filtered.map((u) => u.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(visibleIds)));
  const applyBulkGrupo = async () => {
    try {
      const r = await adminApi.bulkGrupo([...selected], bulkGrupoVal.trim());
      toast.success(`Grupo "${r.grupo || "(sin grupo)"}" asignado a ${r.modificados} usuario(s).`);
      setSelected(new Set()); setBulkGrupoVal(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try { await adminApi.deleteUser(toDelete.id); toast.success("Usuario eliminado definitivamente."); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const darBaja = async (u) => {
    try { await adminApi.updateUser(u.id, { rol_supera: "Baja" }); toast.success(`${u.nombre} marcado como Baja.`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const stateBadge = (u) => {
    if (u.suspendido_manual) return ["Suspensión manual", "#8E44AD"];
    if (u.suspendido_pago) return ["Suspensión por pago", "#C0392B"];
    if (u.rol_supera === "Baja") return ["Baja", "#6B7688"];
    return ["Activo", "#22A860"];
  };

  const adminAccess = (u) => {
    const labels = { super_admin: "Super Admin", admin: "Super Admin", manager: "Manager", staff: "Staff" };
    return labels[u.role] || null;
  };

  return (
    <div data-testid="users-admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0A1C30]">Usuarios</h1>
          <p className="text-[#6B7688] mt-1">Organiza por categorías, asigna roles y gestiona suspensiones.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="manage-roles-btn" onClick={() => setManageRoles(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-2 text-xs"><Tags className="w-4 h-4" /> Gestionar roles</button>
          <button data-testid="manage-segments-btn" onClick={() => setManageSegs(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-2 text-xs"><Settings2 className="w-4 h-4" /> Gestionar categorías</button>
        </div>
      </div>

      {/* Segment tabs */}
      <div className="mt-5 flex gap-1 border-b border-black/5 overflow-x-auto" data-testid="user-segments">
        {segments.map((s) => (
          <button key={s.id || s.tipo} data-testid={`seg-${s.tipo}${s.rol ? "-" + s.rol : ""}`} onClick={() => setActive(s)}
            className={`px-4 py-2.5 font-bold text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${active && (active.id === s.id) ? "border-[var(--gold)] text-[#0A1C30]" : "border-transparent text-[#6B7688] hover:text-[#0A1C30]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <div className="w-full sm:w-72"><Input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email, programa..." /></div>
        <Select value={periodo || "all"} onValueChange={(v) => setPeriodo(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="users-periodo" className="w-48"><SelectValue placeholder="Periodo" /></SelectTrigger>
          <SelectContent>{PERIODOS.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-[#6B7688] font-bold">{filtered.length} usuario(s)</span>
      </div>

      {/* Bulk group assignment bar */}
      {selected.size > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl bg-[#0A1C30] px-4 py-3 text-white" data-testid="bulk-grupo-bar">
          <span className="text-sm font-extrabold" data-testid="bulk-selected-count">{selected.size} seleccionado(s)</span>
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Tags className="w-4 h-4 text-[var(--gold)] shrink-0" />
            <select data-testid="bulk-grupo-input" value={bulkGrupoVal} onChange={(e) => setBulkGrupoVal(e.target.value)}
              className="h-10 rounded-lg bg-white/10 border border-white/15 text-white px-3 text-sm flex-1">
              <option value="" className="text-[#0A1C30]">Sin grupo</option>
              {GRUPO_LETRAS.map((g) => <option key={g} value={g} className="text-[#0A1C30]">Grupo {g}</option>)}
            </select>
          </div>
          <button data-testid="bulk-grupo-apply" onClick={applyBulkGrupo}
            className="rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-5 py-2.5 text-sm transition-colors">
            Asignar grupo
          </button>
          <button data-testid="bulk-grupo-clear" onClick={() => setSelected(new Set())}
            className="rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2.5 text-sm transition-colors">
            Quitar selección
          </button>
        </div>
      )}

      <div className="mt-4 bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 bg-[#FAFAFA]">
                <th className="px-5 py-3.5 w-10">
                  <Checkbox data-testid="bulk-select-all" checked={allSelected} onCheckedChange={toggleAll} aria-label="Seleccionar todos" />
                </th>
                {["Nombre", "Email", "WhatsApp", "Ubicación", "Programa", "Rol Supera", "Grupo", "Estado", "Pagos", "Último acceso", "Registro"].map((h) => (
                  <th key={h} className="px-4 py-3.5 text-xs font-extrabold tracking-wide text-[#6B7688] uppercase whitespace-nowrap">{h}</th>
                ))}
                <th className="px-5 py-3.5 text-right text-xs font-extrabold tracking-wide text-[#6B7688] uppercase sticky right-0 bg-[#FAFAFA] shadow-[-8px_0_12px_-10px_rgba(0,0,0,0.25)]">Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="users-list">
              {loading && <tr><td colSpan={13} className="px-5 py-10 text-center text-[#6B7688]">Cargando...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={13} className="px-5 py-10 text-center text-[#6B7688]">Sin usuarios en esta categoría.</td></tr>}
              {!loading && filtered.map((u) => {
                const [badge, color] = stateBadge(u);
                return (
                  <tr key={u.id} className={`border-b border-black/5 last:border-0 hover:bg-[#FAFAFA] ${selected.has(u.id) ? "bg-[#FBF7E8]" : ""}`}>
                    <td className="px-5 py-4">
                      <Checkbox data-testid={`bulk-select-${u.email}`} checked={selected.has(u.id)} onCheckedChange={() => toggleSel(u.id)} aria-label={`Seleccionar ${u.nombre}`} />
                    </td>
                    <td className="px-5 py-4">
                      <button data-testid={`user-name-${u.email}`} onClick={() => setFichaId(u.id)} className="text-sm font-bold text-[#0A1C30] hover:text-[#3B7DF6] text-left transition-colors">{u.nombre} {u.apellidos}</button>
                      {adminAccess(u) && (
                        <span data-testid={`admin-access-badge-${u.email}`} className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide bg-[#FBF7E8] text-[#B99B2E] ring-1 ring-[var(--gold)]" title={`Acceso admin: ${adminAccess(u)}`}>
                          <ShieldCheck className="w-3 h-3" /> {adminAccess(u)}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-[#2A3446]">{u.email}</td>
                    <td className="px-5 py-4 text-sm text-[#2A3446] whitespace-nowrap">{u.whatsapp || "—"}</td>
                    <td className="px-5 py-4 text-sm text-[#2A3446]">{[u.ciudad, u.pais].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-5 py-4 text-sm text-[#2A3446]">{u.programa || "—"}</td>
                    <td className="px-5 py-4">
                      {adminAccess(u)
                        ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-[#FBF0CE] text-[#B99B2E]"><ShieldCheck className="w-3.5 h-3.5" /> {u.rol_supera || adminAccess(u)}</span>
                        : <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-[#EDF0F4] text-[#2A3446]">{u.rol_supera || "En Reclutamiento"}</span>}
                    </td>
                    <td className="px-5 py-4 text-sm text-[#2A3446]">{u.grupo ? <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-[#EAF1FF] text-[#3B7DF6]">{u.grupo}</span> : <span className="text-[#B7BFCC]">—</span>}</td>
                    <td className="px-5 py-4"><span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold text-white" style={{ background: color }}>{badge}</span></td>
                    <td className="px-5 py-4"><PagoBadge estado={u.estado_pago} testid={`user-pago-${u.email}`} /></td>
                    <td className="px-5 py-4 text-xs text-[#6B7688] whitespace-nowrap" data-testid={`user-last-${u.email}`}>{fmtFecha(u.last_access || u.last_login) || "Sin registro"}</td>
                    <td className="px-5 py-4 text-xs text-[#6B7688] whitespace-nowrap">{fmtFecha(u.created_at)}</td>
                    <td className="px-5 py-4 text-right whitespace-nowrap sticky right-0 bg-white shadow-[-8px_0_12px_-10px_rgba(0,0,0,0.25)]">
                      <button data-testid={`user-ficha-${u.email}`} onClick={() => setFichaId(u.id)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#EDF0F4] text-[#0A1C30] transition-colors" title="Ver ficha completa"><IdCard className="w-4 h-4" /></button>
                      <button data-testid={`user-edit-${u.email}`} onClick={() => setEditing(u)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#EDF0F4] text-[#3B7DF6] transition-colors" title="Editar"><Pencil className="w-4 h-4" /></button>
                      {canRbac && (
                        <button data-testid={`user-rbac-${u.email}`} onClick={() => setRbacUser(u)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#FBF0CE] text-[#B99B2E] transition-colors ml-1" title="Permisos de administración"><ShieldCheck className="w-4 h-4" /></button>
                      )}
                      {u.rol_supera !== "Baja" && u.role !== "admin" && (
                        <button data-testid={`user-baja-${u.email}`} onClick={() => darBaja(u)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#F1E9F7] text-[#8E44AD] transition-colors ml-1" title="Dar de baja (conservar)"><UserX className="w-4 h-4" /></button>
                      )}
                      <button data-testid={`user-del-${u.email}`} onClick={() => setToDelete(u)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#FCE4E4] text-[#E23B3B] transition-colors ml-1" title="Eliminar definitivamente"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <UserFichaDialog userId={fichaId} onClose={() => setFichaId(null)} />
      {editing && <EditUserDialog user={editing} roles={roles} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {rbacUser && <RbacDialog user={rbacUser} onClose={() => setRbacUser(null)} onSaved={() => { setRbacUser(null); load(); }} />}
      {manageRoles && <RolesManager roles={roles} onClose={() => setManageRoles(false)} onChanged={loadMeta} />}
      {manageSegs && <SegmentsManager roles={roles} onClose={() => setManageSegs(false)} onChanged={loadMeta} />}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="user-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar definitivamente a {toDelete?.nombre}?</AlertDialogTitle>
            <AlertDialogDescription>Esto borra la cuenta de la base de datos y no se puede deshacer. Si solo quieres conservar el historial, usa "Dar de baja".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm-btn" onClick={confirmDelete} className="bg-[#E23B3B] hover:bg-[#c72f2f]">Eliminar definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditUserDialog({ user, roles, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: user.nombre || "", apellidos: user.apellidos || "", programa: user.programa || "", pais: user.pais || "",
    ciudad: user.ciudad || "", whatsapp: user.whatsapp || "", edad: user.edad || "", colegio: user.colegio || "", grado: user.grado || "",
    role: user.role || "user", rol_supera: user.rol_supera || "En Reclutamiento", puntos: user.puntos || 0,
    grupo: user.grupo || "", sexo: user.sexo || "",
    suspendido_manual: !!user.suspendido_manual, suspension_manual_reason: user.suspension_manual_reason || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const roleNames = roles.map((r) => r.nombre);
  if (form.rol_supera && !roleNames.includes(form.rol_supera)) roleNames.unshift(form.rol_supera);
  const save = async () => {
    try {
      await adminApi.updateUser(user.id, { ...form, puntos: Number(form.puntos) });
      toast.success("Usuario actualizado"); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-edit-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold text-[#0A1C30]">Editar usuario</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Nombre</Label><Input data-testid="edit-nombre" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Apellidos</Label><Input data-testid="edit-apellidos" value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">WhatsApp</Label><Input data-testid="edit-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Edad</Label><Input data-testid="edit-edad" value={form.edad} onChange={(e) => set("edad", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">País</Label><Input data-testid="edit-pais" value={form.pais} onChange={(e) => set("pais", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Ciudad</Label><Input data-testid="edit-ciudad" value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Colegio</Label><Input data-testid="edit-colegio" value={form.colegio} onChange={(e) => set("colegio", e.target.value)} /></div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Grado académico</Label><Input data-testid="edit-grado" value={form.grado} onChange={(e) => set("grado", e.target.value)} placeholder="Ej. 5° de primaria" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Rol Supera</Label>
              <Select value={form.rol_supera} onValueChange={(v) => set("rol_supera", v)}>
                <SelectTrigger data-testid="edit-rol-supera" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{roleNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Acceso al sistema</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuario</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="instructor">Instructor</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="invitado">Invitado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Programa</Label>
              <Select value={form.programa || "none"} onValueChange={(v) => set("programa", v === "none" ? "" : v)}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Sin programa" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Sin programa</SelectItem>{programs.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Puntos</Label><Input type="number" value={form.puntos} onChange={(e) => set("puntos", e.target.value)} /></div>
          </div>
          <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Grupo <span className="font-normal text-[#6B7688]">(A–Z · define qué contenido por grupo verá: libros, materias, clases, avisos y reflexiones)</span></Label>
            <select data-testid="edit-grupo" value={form.grupo} onChange={(e) => set("grupo", e.target.value)} className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm bg-white">
              <option value="">Sin grupo (ve solo contenido para "Todos los grupos")</option>
              {GRUPO_LETRAS.map((g) => <option key={g} value={g}>Grupo {g}</option>)}
            </select>
          </div>
          <div><Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Sexo <span className="font-normal text-[#6B7688]">(Ana lo usa para decir “tu hijo” o “tu hija”)</span></Label>
            <select data-testid="edit-sexo" value={form.sexo} onChange={(e) => set("sexo", e.target.value)} className="w-full rounded-lg ring-1 ring-black/10 px-3 py-2 text-sm bg-white">
              <option value="">Sin indicar</option>
              <option value="hombre">Hombre (hijo / candidato)</option>
              <option value="mujer">Mujer (hija / candidata)</option>
            </select>
          </div>
          <div className="rounded-xl bg-[#F4F6F9] p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div><p className="font-bold text-sm text-[#0A1C30] flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#8E44AD]" /> Suspensión temporal manual</p><p className="text-xs text-[#6B7688]">Bloquea el acceso a la app hasta que la desactives.</p></div>
              <Switch data-testid="edit-suspender-manual" checked={form.suspendido_manual} onCheckedChange={(v) => set("suspendido_manual", v)} />
            </div>
            {form.suspendido_manual && <Textarea data-testid="edit-suspension-reason" rows={2} value={form.suspension_manual_reason} onChange={(e) => set("suspension_manual_reason", e.target.value)} placeholder="Motivo de la suspensión (lo verá el usuario)." />}
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-5 py-2.5 font-bold text-[#6B7688] hover:bg-[#EDF0F4] transition-colors">Cancelar</button>
          <button data-testid="edit-save" onClick={save} className="rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-6 py-2.5 transition-colors">Guardar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const RBAC_ROLES = [
  ["super_admin", "Super Admin", "Acceso total a todo el panel."],
  ["manager", "Manager", "Acceso a las secciones que marques abajo."],
  ["staff", "Staff", "Acceso limitado a las secciones que marques."],
  ["user", "Quitar acceso", "Usuario normal, sin acceso al panel admin."],
];

function RbacDialog({ user, onClose, onSaved }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialRole = ["super_admin", "admin"].includes(user.role) ? "super_admin"
    : ["manager", "staff"].includes(user.role) ? user.role : "user";
  const baseRole = ["super_admin", "admin", "manager", "staff"].includes(user.role) ? "user" : (user.role || "user");
  const [role, setRole] = useState(initialRole);
  const [permisos, setPermisos] = useState(new Set(user.permisos || []));

  useEffect(() => {
    adminApi.rbacSections()
      .then((s) => setSections(s))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const isLimited = role === "manager" || role === "staff";
  const toggle = (key) => setPermisos((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const selectAll = () => setPermisos(new Set(sections.map((s) => s.key)));
  const clearAll = () => setPermisos(new Set());

  const save = async () => {
    setSaving(true);
    try {
      if (role === "user") {
        await adminApi.updateUser(user.id, { role: baseRole, permisos: [] });
      } else {
        await adminApi.setUserRbac(user.id, { role, permisos: role === "super_admin" ? [] : [...permisos] });
      }
      toast.success("Permisos actualizados.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rbac-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-[#0A1C30] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[var(--gold)]" /> Permisos de administración
          </DialogTitle>
          <p className="text-sm text-[#6B7688]">{user.nombre} {user.apellidos} · {user.email}</p>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label className="text-sm font-bold text-[#0A1C30] mb-2 block">Nivel de acceso</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {RBAC_ROLES.map(([val, label, desc]) => (
                <button key={val} type="button" data-testid={`rbac-role-${val}`} onClick={() => setRole(val)}
                  className={`text-left rounded-xl border-2 px-4 py-3 transition-colors ${role === val ? "border-[var(--gold)] bg-[#FBF7E8]" : "border-black/5 hover:border-black/15 bg-white"}`}>
                  <p className="font-extrabold text-sm text-[#0A1C30]">{label}</p>
                  <p className="text-xs text-[#6B7688] mt-0.5">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {role === "super_admin" && (
            <div className="rounded-xl bg-[#FBF0CE] text-[#8a6d10] text-sm font-bold px-4 py-3" data-testid="rbac-superadmin-note">
              Este usuario tendrá acceso completo a todas las secciones del panel.
            </div>
          )}

          {isLimited && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-bold text-[#0A1C30]">Secciones permitidas ({permisos.size})</Label>
                <div className="flex gap-2">
                  <button type="button" data-testid="rbac-select-all" onClick={selectAll} className="rounded-lg bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-1.5 text-xs hover:bg-[#EDF0F4]">Seleccionar todo</button>
                  <button type="button" data-testid="rbac-clear-all" onClick={clearAll} className="rounded-lg bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-1.5 text-xs hover:bg-[#EDF0F4]">Quitar todo</button>
                </div>
              </div>
              {loading ? (
                <p className="text-sm text-[#6B7688] py-4">Cargando secciones...</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                  {sections.map((s) => (
                    <label key={s.key} data-testid={`rbac-section-${s.key}`}
                      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${permisos.has(s.key) ? "border-[var(--gold)] bg-[#FBF7E8]" : "border-black/5 hover:bg-[#FAFAFA]"}`}>
                      <Checkbox checked={permisos.has(s.key)} onCheckedChange={() => toggle(s.key)} />
                      <span className="text-sm font-bold text-[#0A1C30]">{s.label}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#6B7688] mt-2">El Dashboard siempre está disponible para cualquier administrador.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-5 py-2.5 font-bold text-[#6B7688] hover:bg-[#EDF0F4] transition-colors">Cancelar</button>
          <button data-testid="rbac-save" onClick={save} disabled={saving} className="rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-6 py-2.5 transition-colors disabled:opacity-50">{saving ? "Guardando..." : "Guardar permisos"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RolesManager({ roles, onClose, onChanged }) {
  const [items, setItems] = useState(roles);
  const [nuevo, setNuevo] = useState("");
  const reload = () => adminApi.userRoles.list().then((r) => { setItems(r); onChanged && onChanged(); }).catch((e) => toast.error(apiError(e)));
  const add = async () => { if (!nuevo.trim()) return; try { await adminApi.userRoles.create({ nombre: nuevo.trim() }); setNuevo(""); toast.success("Rol agregado."); reload(); } catch (e) { toast.error(apiError(e)); } };
  const rename = async (it, nombre) => { try { await adminApi.userRoles.update(it.id, { nombre }); toast.success("Rol actualizado."); reload(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (it) => { if (!window.confirm(`¿Eliminar el rol "${it.nombre}"?`)) return; try { await adminApi.userRoles.remove(it.id); toast.success("Rol eliminado."); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="roles-manager">
        <DialogHeader><DialogTitle>Gestionar roles</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="role-new-input" value={nuevo} onChange={(e) => setNuevo(e.target.value)} placeholder="Nuevo rol (ej. Voluntario)" />
          <button data-testid="role-add-btn" onClick={add} className="rounded-xl bg-navy text-white font-bold px-4 text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="mt-2 divide-y divide-black/5 max-h-80 overflow-y-auto" data-testid="roles-list">
          {items.map((it) => <RoleRow key={it.id} it={it} onRename={rename} onRemove={remove} />)}
          {items.length === 0 && <p className="text-sm text-[#6B7688] py-4">Sin roles.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoleRow({ it, onRename, onRemove }) {
  const [val, setVal] = useState(it.nombre);
  return (
    <div className="flex items-center gap-2 py-2">
      <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-9" />
      <button onClick={() => onRename(it, val.trim())} disabled={val.trim() === it.nombre || !val.trim()} className="rounded-lg bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-1.5 text-xs disabled:opacity-40">Guardar</button>
      <button data-testid={`role-del-${it.nombre}`} onClick={() => onRemove(it)} className="p-1.5 rounded-lg text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function SegmentsManager({ roles, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", tipo: "rol", rol: roles[0]?.nombre || "" });
  const reload = () => adminApi.userSegments.list().then((s) => { setItems(s); onChanged && onChanged(); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { adminApi.userSegments.list().then(setItems).catch(() => {}); }, []);
  const add = async () => {
    if (!form.label.trim()) { toast.error("Escribe el nombre de la categoría."); return; }
    try { await adminApi.userSegments.create({ label: form.label.trim(), tipo: form.tipo, rol: form.tipo === "rol" ? form.rol : "" }); setForm({ label: "", tipo: "rol", rol: roles[0]?.nombre || "" }); toast.success("Categoría agregada."); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (it) => { try { await adminApi.userSegments.update(it.id, { activo: it.activo === false }); reload(); } catch (e) { toast.error(apiError(e)); } };
  const rename = async (it, label) => { try { await adminApi.userSegments.update(it.id, { label }); toast.success("Categoría actualizada."); reload(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (it) => { if (!window.confirm(`¿Eliminar la categoría "${it.label}"?`)) return; try { await adminApi.userSegments.remove(it.id); toast.success("Categoría eliminada."); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="segments-manager">
        <DialogHeader><DialogTitle>Gestionar categorías</DialogTitle></DialogHeader>
        <div className="rounded-xl bg-[#F4F6F9] p-3 space-y-2">
          <Label className="text-xs font-bold">Nueva categoría</Label>
          <Input data-testid="seg-new-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Nombre de la categoría" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
              <SelectTrigger data-testid="seg-new-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPO_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {form.tipo === "rol" && (
              <Select value={form.rol} onValueChange={(v) => setForm((f) => ({ ...f, rol: v }))}>
                <SelectTrigger data-testid="seg-new-rol"><SelectValue placeholder="Rol" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.nombre}>{r.nombre}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <button data-testid="seg-add-btn" onClick={add} className="w-full rounded-xl bg-navy text-white font-bold py-2 text-sm inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Agregar categoría</button>
        </div>
        <div className="mt-2 divide-y divide-black/5 max-h-72 overflow-y-auto" data-testid="segments-list">
          {items.map((it) => <SegRow key={it.id} it={it} onToggle={toggle} onRename={rename} onRemove={remove} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SegRow({ it, onToggle, onRename, onRemove }) {
  const [val, setVal] = useState(it.label);
  return (
    <div className="flex items-center gap-2 py-2">
      <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-9" />
      <span className="text-[11px] text-[#6B7688] font-bold whitespace-nowrap">{it.tipo}{it.rol ? `:${it.rol}` : ""}</span>
      <Switch checked={it.activo !== false} onCheckedChange={() => onToggle(it)} />
      <button onClick={() => onRename(it, val.trim())} disabled={val.trim() === it.label || !val.trim()} className="rounded-lg bg-[#F4F6F9] text-[#0A1C30] font-bold px-2.5 py-1.5 text-xs disabled:opacity-40">OK</button>
      <button data-testid={`seg-del-${it.label}`} onClick={() => onRemove(it)} className="p-1.5 rounded-lg text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}
